import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Snackbar, Table, TableBody, TableCell, TableHead, TablePagination, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { baseLanguageCodeOf } from '../../generic/utilities/customLanguage'
import { getTerms, getHistory, getSystemValues } from './languageTermsApi'
import { indexSystemValues, NO_SYSTEM_VALUES } from './systemValueTerms'
import { loadLanguageCatalogueArtifact } from './artifactLoader'
import { matchesSearch } from './termFilters'
import { classify } from './termClass'
import TermRow from './TermRow'
import { usageChipLabel } from './UsageChip'
import LanguageHistoryDialog from './LanguageHistoryDialog'
import { rejectionMessageKeyFor } from './termsRejectionMap'

// RN6f, DG2-0: the four exclusive filters, "All" (the labels catalogue) first, as the default.
const FILTERS = [
  { id: 'all', labelKey: 'All' },
  { id: 'onlyCustomised', labelKey: 'Only customised' },
  { id: 'onlyDetached', labelKey: 'Only detached' },
  { id: 'onlyOutsideCatalogue', labelKey: 'Only outside the catalogue' }
]

/**
 * The inherited term of a key, for the language BEING EDITED — never for the interface language of
 * the superadmin doing the editing (S82's trap). Falls from the base of the edited language, to
 * `en`, to the key itself (03-dominio.md §6 "Heredado").
 *
 * @param {Object} i18n i18next instance (from `useSmartTranslation`)
 * @param {string} editedLanguageCode The custom language code being opened (e.g. `es-acme`)
 * @param {string} key The i18n key
 * @returns {string} The inherited term
 */
// DC-36: table-layout: fixed ignores minWidth, so the Usage column gets a declared width estimated from the longest chip
// label of the visible page (about 7 px per character + 48 px of chip and cell padding), never below the 160 px of B7.
const USAGE_MIN_WIDTH = 160
const usageWidthFor = (labels) => Math.max(USAGE_MIN_WIDTH, ...labels.map((label) => label.length * 7 + 48))

const inheritedTermOf = (i18n, editedLanguageCode, key) => {
  const base = baseLanguageCodeOf(editedLanguageCode)
  const fromBase = i18n.getResource(base, 'translation', key)
  if (fromBase !== undefined) return fromBase
  const fromEnglish = i18n.getResource('en', 'translation', key)
  if (fromEnglish !== undefined) return fromEnglish
  return key
}

/**
 * Historia 176 — CU2 (DC-8). The detail of the "Custom languages" tab: the catalogue of texts of
 * the custom language picked in the list above it. It opens the catalogue of `code` when it mounts;
 * the list gives it a new `key` per language, so changing the selection mounts a fresh detail.
 *
 * @param {{ code: string, catalogue: Array }} props The selected custom language and the languages
 *   catalogue the list already read (`GET languages`).
 */
const LanguageTexts = ({ code, catalogue = [] }) => {
  const { t_, i18n } = useSmartTranslation()
  // `{}` until the catalogue is read; `artifactLoaded` / `termsLoaded` tell the two apart.
  const [artifact, setArtifact] = useState({})
  const artifactLoaded = 'labels' in artifact
  const selectedCode = code
  const [termsResponse, setTermsResponse] = useState({})
  const termsLoaded = 'terms' in termsResponse
  const [searchInput, setSearchInput] = useState('')
  // RN6i, CD-19: the search is applied 300 ms after the last keystroke; while typing nothing is recomputed.
  const [searchQuery, setSearchQuery] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 300)
    return () => clearTimeout(timer)
  }, [searchInput])
  const [filter, setFilter] = useState('all')
  // RN6i, CD-20: 50 rows per page unless the superadmin picks 25 or 100; never persisted (RN6j).
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  // Story 204 (RN6.1, RN6.2, H8): the system values of C5, their own load error and the page of their block.
  const [systemValues, setSystemValues] = useState(NO_SYSTEM_VALUES)
  const [systemValuesErrorKey, setSystemValuesErrorKey] = useState('')
  const [systemPage, setSystemPage] = useState(0)
  // Changing the applied search or the filter goes back to the first page (RN6i), in both tables.
  useEffect(() => {
    setPage(0)
    setSystemPage(0)
  }, [searchQuery, filter])
  // RN8f: the popover of a Usage chip closes when the page, its size, the filter or the applied search change.
  const [openUsageKey, setOpenUsageKey] = useState('')
  // RN6h: one cell in edition at a time.
  const [editingKey, setEditingKey] = useState('')
  // RN6i: the cell in edition is cancelled together with the popover on any change of view.
  useEffect(() => {
    setOpenUsageKey('')
    setEditingKey('')
  }, [page, systemPage, rowsPerPage, filter, searchQuery])
  // RN6l: the brief success notice (T47/T48/T49), closed by itself after 3 s.
  const [notice, setNotice] = useState('')
  // T7 (S120): the global "Language history" (T40), independent of any row still being on screen.
  const [historyOpen, setHistoryOpen] = useState(false)
  const [historyEntries, setHistoryEntries] = useState([])
  // T7 (S123): a rejected `GET .../terms` explains itself with the same map as write rejections.
  const [openErrorKey, setOpenErrorKey] = useState('')
  // T7 (S153): a 409 on any write puts the whole tab read-only until the catalogue is reopened.
  const [fileUnavailable, setFileUnavailable] = useState(false)

  const openCatalogue = useCallback(async (code) => {
    setTermsResponse({})
    setOpenErrorKey('')
    setFileUnavailable(false)
    // Story 204 (decision 10, H8): the system values load next to the terms but on their own: a 403 or a network
    // failure shows its error inside their block and leaves the labels catalogue open and editable.
    setSystemValuesErrorKey('')
    getSystemValues()
      .then((response) => setSystemValues(indexSystemValues(response)))
      .catch((error) => setSystemValuesErrorKey(rejectionMessageKeyFor(error)))
    try {
      const [artifactModule, terms] = await Promise.all([
        artifactLoaded ? Promise.resolve({ default: artifact }) : loadLanguageCatalogueArtifact(),
        getTerms(code)
      ])
      setArtifact((current) => ('labels' in current ? current : artifactModule.default))
      // An empty answer leaves the detail as not loaded, as the initial `{}` does.
      setTermsResponse(terms || {})
    } catch (error) {
      setOpenErrorKey(rejectionMessageKeyFor(error))
    }
  }, [artifact, artifactLoaded])

  // T7 (S114): reflects what Sun's `TermResultDto.value` says is left for a key, without
  // rereading the catalogue.
  const updateTerm = useCallback((key, value) => {
    setTermsResponse((current) => {
      if (!('terms' in current)) return current
      const nextTerms = { ...current.terms }
      if (value === null) {
        delete nextTerms[key]
      } else {
        nextTerms[key] = value
      }
      return { ...current, terms: nextTerms }
    })
  }, [])

  // Once, when the detail mounts: `openCatalogue` changes identity as soon as the artifact arrives,
  // and reopening then would throw away what the superadmin is doing.
  useEffect(() => {
    openCatalogue(code)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // CU7 E1/E2: a history that cannot be read explains itself with the rejection map, inside the dialog.
  const [historyErrorKey, setHistoryErrorKey] = useState('')
  const loadLanguageHistory = useCallback(async () => {
    try {
      setHistoryEntries(await getHistory(selectedCode))
      setHistoryErrorKey('')
    } catch (error) {
      setHistoryErrorKey(rejectionMessageKeyFor(error))
    }
  }, [selectedCode])

  const openLanguageHistory = useCallback(async () => {
    await loadLanguageHistory()
    setHistoryOpen(true)
  }, [loadLanguageHistory])

  // 03-dominio.md §6 "Clase de una clave" (I9, RN22, RN24): pure classify() over facts read from
  // the build artifact and from the `en` bundle, never a lookup of its own.
  const classOf = useCallback((key) => artifactLoaded
    ? classify(key, {
      isSystemValue: systemValues.has(key),
      isLabel: artifact.labels.some((label) => label.key === key),
      inEnJson: i18n.getResource('en', 'translation', key) !== undefined,
      inUsedKeys: artifact.usedKeys.includes(key)
    })
    : 'label', [artifact, artifactLoaded, i18n, systemValues])

  // The rows of the active filter, searched over the WHOLE list (RN6i). Recomputed only when the
  // catalogue, the filter or the applied search change, never per keystroke (S159).
  const rows = useMemo(() => {
    if (!artifactLoaded || !termsLoaded) return []
    // "All" (RN6f, default): the labels catalogue, whether redefined or not (S85).
    const labelRows = artifact.labels.map((label) => {
      const key = label.key
      const hasCustom = Object.prototype.hasOwnProperty.call(termsResponse.terms, key)
      const inherited = inheritedTermOf(i18n, selectedCode, key)
      const custom = hasCustom ? termsResponse.terms[key] : undefined
      return {
        key,
        inherited,
        custom,
        // RN14b: exact comparison, no trimming (S84).
        sameAsInherited: hasCustom && custom === inherited,
        termClass: 'label'
      }
    })
    // "Only customised"/"Only detached"/"Only outside the catalogue" (RN6c-RN6e): every redefined
    // key ("terms"), each with its class (S92-S94).
    const customisedRows = Object.keys(termsResponse.terms).map((key) => {
      const inherited = inheritedTermOf(i18n, selectedCode, key)
      const custom = termsResponse.terms[key]
      return {
        key,
        inherited,
        custom,
        sameAsInherited: custom === inherited,
        termClass: classOf(key),
        domains: systemValues.domainsOf(key)
      }
    })
    const rowsByFilter = {
      all: labelRows,
      onlyCustomised: customisedRows,
      onlyDetached: customisedRows.filter((row) => row.termClass === 'detached'),
      onlyOutsideCatalogue: customisedRows.filter((row) => row.termClass === 'outsideCatalogue')
    }
    // RN6b: search over standard, inherited and custom (S89); empty/whitespace = no filter (S90).
    return rowsByFilter[filter].filter((row) => matchesSearch(searchQuery, [row.key, row.inherited, row.custom || '']))
  }, [artifact, artifactLoaded, termsResponse, termsLoaded, selectedCode, filter, searchQuery, i18n, classOf, systemValues])

  // Story 204 (RN6.2, RN6.8, decision 11): the rows of the "System values" block, one per distinct code (homonyms
  // grouped with their domains), searched like the labels. Only painted under the "All" filter.
  const systemRows = useMemo(() => {
    if (!termsLoaded) return []
    return systemValues.rows.map(({ code: key, domains }) => {
      const hasCustom = Object.prototype.hasOwnProperty.call(termsResponse.terms, key)
      const inherited = inheritedTermOf(i18n, selectedCode, key)
      const custom = hasCustom ? termsResponse.terms[key] : undefined
      return { key, inherited, custom, sameAsInherited: hasCustom && custom === inherited, termClass: 'systemValue', domains }
    }).filter((row) => matchesSearch(searchQuery, [row.key, row.inherited, row.custom || '']))
  }, [systemValues, termsResponse, termsLoaded, selectedCode, searchQuery, i18n])

  // T28/P2: {{base}} is the NAME of the base language (GET languages `name`), never its code.
  const selectedLanguage = catalogue.find((language) => language.code === selectedCode)
  const baseStandardLanguage = catalogue.find((language) => language.code === selectedLanguage?.baseLanguageCode)
  const replacesBase = selectedLanguage?.replacesBase === true

  // RN25/E3: a MISSING or INVALID file means read-only until it's fixed. Same wording as the 409
  // reused later for a write attempt against a file that turned unreadable mid-session (E3).
  const FILE_STATE_WARNING_KEY = {
    MISSING: 'The language file is missing: nothing can be saved until it is restored',
    INVALID: 'The language file cannot be read: nothing can be saved until it is fixed'
  }
  const fileStateWarningKey = fileUnavailable
    ? 'The language file cannot be read: nothing can be saved until it is fixed'
    : (termsLoaded ? FILE_STATE_WARNING_KEY[termsResponse.fileState] : undefined)

  // RN25: no write action of any kind while the file cannot be read, whether that came from the
  // GET at opening (MISSING/INVALID) or from a 409 mid-session (S153).
  const readOnly = fileUnavailable || (termsLoaded ? termsResponse.fileState !== 'READABLE' : false)

  const visibleRows = rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
  const usageWidth = usageWidthFor(visibleRows.map((row) => usageChipLabel(t_, row.key, artifact, systemValues)))
  const visibleSystemRows = systemRows.slice(systemPage * rowsPerPage, (systemPage + 1) * rowsPerPage)
  const systemUsageWidth = usageWidthFor(visibleSystemRows.map((row) => usageChipLabel(t_, row.key, artifact, systemValues)))

  // One row of either table: the same TermRow, the same edition, history and notices (RN6.6).
  const termRowFor = (row) => (
    <TermRow
      key={row.key}
      row={row}
      code={selectedCode}
      artifact={artifact}
      systemValues={systemValues}
      readOnly={readOnly}
      editing={editingKey === row.key}
      onStartEdit={() => setEditingKey(row.key)}
      // A row that finishes saving must not close the cell another row has in edition meanwhile.
      onStopEdit={() => setEditingKey((current) => (current === row.key ? '' : current))}
      onTermChanged={updateTerm}
      onFileUnavailable={() => setFileUnavailable(true)}
      onSuccess={setNotice}
      usageOpen={openUsageKey === row.key}
      onUsageOpen={() => setOpenUsageKey(row.key)}
      onUsageClose={() => setOpenUsageKey('')}
    />
  )

  // The five columns of #176, shared by the labels table and the "System values" block (S110).
  const tableHead = (width) => (
    <TableHead>
      <TableRow>
        {/* RN6i: fixed widths, so filtering, searching or an empty result never move the header. */}
        {/* B1 (RN6g): Usage and Actions are fixed in px, because table-layout: fixed ignores minWidth and a percentage left Actions ~77 px at 1512 px. The three text columns share what is left equally (20 % each, O-8a); the cell in edition does not clip the text, the field scrolls it (S189). */}
        <TableCell sx={{ width: '20%' }}>{t_('Standard term')}</TableCell>
        <TableCell sx={{ width: '20%' }}>{t_('Inherited')}</TableCell>
        <TableCell sx={{ width: '20%' }}>{t_('Customised')}</TableCell>
        {/* B7: room for "Common structure" / "Estructura común"; the chip itself never cuts its label either. */}
        <TableCell sx={{ width, minWidth: width }}>{t_('Usage')}</TableCell>
        <TableCell sx={{ width: 112, minWidth: 112, whiteSpace: 'nowrap' }}>{t_('Actions')}</TableCell>
      </TableRow>
    </TableHead>
  )

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h6'>{selectedCode}</Typography>

      {selectedCode !== '' && replacesBase
        ? (
          <Alert severity='info'>
            {t_('This language replaces {{base}}: every user of {{base}} will see these terms', {
              base: baseStandardLanguage?.name || ''
            })}
          </Alert>
          )
        : null}

      {openErrorKey !== ''
        ? <Alert severity='error'>{t_(openErrorKey)}</Alert>
        : null}

      {fileStateWarningKey !== undefined
        ? <Alert severity='warning'>{t_(fileStateWarningKey)}</Alert>
        : null}

      {selectedCode !== '' && termsLoaded
        ? (
          <Box
            data-testid='search-row'
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mt: 2,
              // V8: the tab bar of SmartNavigator is fixed; the search sticks right under it (offset published by
              // SmartNavigator, the same one MaterialReceptionHeader uses) so the floating label is never covered.
              position: 'sticky',
              top: 'var(--smart-navigator-sticky-offset, 48px)',
              zIndex: 10,
              backgroundColor: (theme) => theme.palette.background.paper,
              pt: 1.5,
              pb: 0.5
            }}
          >
            <TextField
              size='small'
              InputLabelProps={{ sx: { maxWidth: 'none', textOverflow: 'clip' } }}
              label={t_('Search a text')}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
            <Button onClick={openLanguageHistory}>{t_('Language history')}</Button>
          </Box>
          )
        : null}

      {/* DC-33: T15 goes below the search box (outside its sticky row), not beside it. */}
      {selectedCode !== '' && termsLoaded
        ? (
          <Typography variant='caption' component='p' sx={{ mt: 0.5 }}>
            {t_('Some labels are built from data and cannot be listed here')}
          </Typography>
          )
        : null}

      {selectedCode !== '' && termsLoaded
        ? (
          <ToggleButtonGroup
            exclusive
            value={filter}
            onChange={(event, next) => { if (next !== null) setFilter(next) }}
            sx={{ mt: 2 }}
          >
            {FILTERS.map(({ id, labelKey }) => (
              <ToggleButton key={id} value={id}>{t_(labelKey)}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          )
        : null}

      {filter === 'onlyDetached'
        ? (
          <Alert severity='info'>
            {t_('These keys are not in the dictionary of this version nor written in its code. A text built from data may still use one: deleting it brings back the original')}
          </Alert>
          )
        : null}

      {selectedCode !== '' && termsLoaded
        ? (
          <Table size='small' sx={{ tableLayout: 'fixed' }}>
            {tableHead(usageWidth)}
            <TableBody>
              {visibleRows.map(termRowFor)}
            </TableBody>
          </Table>
          )
        : null}

      {selectedCode !== '' && termsLoaded
        ? (
          <TablePagination
            component='div'
            count={rows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(event, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0) }}
            labelRowsPerPage={t_('Rows per page:')}
            // DC-16: MUI sets `title` and `aria-label` of the arrows from here (English by default). Only next/previous are shown.
            getItemAriaLabel={(type) => (type === 'next' ? t_('Go to next page') : type === 'previous' ? t_('Go to previous page') : '')}
            // Language-neutral on purpose ("1–50 / 460"): the contract has no key for "of".
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
          />
          )
        : null}

      {selectedCode !== '' && termsLoaded && rows.length === 0
        ? <Typography>{t_('No texts match the search')}</Typography>
        : null}

      {/* Story 204 (RN6.1, RN6.2, RN6.9, decision 11): the system values Sun sends by code, in their own block under the
          labels, only with "All" (the other filters already list them among the customised keys, S120). A failed load
          shows its error here and nowhere else (H8). */}
      {selectedCode !== '' && termsLoaded && filter === 'all' && (systemValues.loaded || systemValuesErrorKey !== '')
        ? (
          <Box data-testid='system-values-block' sx={{ mt: 4 }}>
            <Typography variant='h6'>{t_('System values')}</Typography>
            <Typography variant='body2' sx={{ mb: 1 }}>
              {t_('Values the system sends by code (statuses, reference data, alarms, errors). A change here applies everywhere the value is shown.')}
            </Typography>
            {systemValuesErrorKey !== ''
              ? <Alert severity='error' data-testid='system-values-error'>{t_(systemValuesErrorKey)}</Alert>
              : (
                <>
                  <Table size='small' sx={{ tableLayout: 'fixed' }}>
                    {tableHead(systemUsageWidth)}
                    <TableBody>
                      {visibleSystemRows.map(termRowFor)}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component='div'
                    count={systemRows.length}
                    page={systemPage}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                    onPageChange={(event, nextPage) => setSystemPage(nextPage)}
                    onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); setSystemPage(0) }}
                    labelRowsPerPage={t_('Rows per page:')}
                    getItemAriaLabel={(type) => (type === 'next' ? t_('Go to next page') : type === 'previous' ? t_('Go to previous page') : '')}
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
                  />
                </>
                )}
          </Box>
          )
        : null}

      <LanguageHistoryDialog
        open={historyOpen}
        code={selectedCode}
        entries={historyEntries}
        loadErrorKey={historyErrorKey}
        terms={termsLoaded ? termsResponse.terms : {}}
        inheritedOf={(key) => inheritedTermOf(i18n, selectedCode, key)}
        readOnly={readOnly}
        artifact={artifact}
        systemValues={systemValues}
        classOf={classOf}
        onClose={() => setHistoryOpen(false)}
        onFileUnavailable={() => setFileUnavailable(true)}
        onRestored={(termKey, value, change) => {
          updateTerm(termKey, value)
          // RN6l: a 200 without change (nothing was written) gives no success notice.
          if (change !== null) setNotice('Value restored')
          // CU8.4: the entry the restore just wrote appears on top, as "Current".
          loadLanguageHistory()
        }}
      />

      <Snackbar
        open={notice !== ''}
        autoHideDuration={3000}
        onClose={(event, reason) => { if (reason !== 'clickaway') setNotice('') }}
      >
        <Alert severity='success' role='status' variant='filled'>{t_(notice)}</Alert>
      </Snackbar>
    </Box>
  )
}

export default LanguageTexts
